import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MultipleChoice,
  MultipleChoiceGroup,
  TemplatesApi,
} from '@ulysses-inc/harami_api_client'
import { baseClient } from 'src/state/middleware/saga/baseClient'
import { handleHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { ExhaustiveError } from 'src/util/error'
import { TemplateEditMode } from '../../context/TemplateEditMode/TemplateEditMode'
import { getTemplateSpecificQueryOptions } from './useQueryTemplateSpecificMultipleChoices'

export type UpdateTemplateSpecificMultipleChoicesParams = {
  id: number
  multipleChoices: MultipleChoice[]
}

/**
 * ひな形限定の選択肢を更新する
 */
export const useMutationUpdateTemplateSpecificMultipleChoices = (
  templateEditMode: TemplateEditMode,
) => {
  const queryClient = useQueryClient()
  const { queryKey } = getTemplateSpecificQueryOptions(templateEditMode)
  return useMutation({
    mutationFn: async ({
      id,
      multipleChoices,
    }: UpdateTemplateSpecificMultipleChoicesParams): Promise<
      MultipleChoice[]
    > => {
      switch (templateEditMode.mode) {
        case 'edit':
          // 編集モード時はtemplateIdを使用してPUTリクエストを送信
          return await baseClient
            .getApi(TemplatesApi)
            .updateTemplateResponseMultipleChoices({
              templateId: templateEditMode.templateId,
              multipleChoiceId: id,
              multipleChoice: multipleChoices,
            })
            .catch(handleHTTPError)
        case 'new':
        case 'newFromPreparedTemplate':
          // 新規作成時は既存のAPI呼び出しを使用
          return await baseClient
            .getApi(TemplatesApi)
            .updateResponseMultipleChoices({
              multipleChoiceId: id,
              multipleChoice: multipleChoices,
            })
            .catch(handleHTTPError)
        default:
          throw new ExhaustiveError(templateEditMode)
      }
    },
    onSuccess: (updatedChoices, { id }) => {
      queryClient.setQueryData<MultipleChoiceGroup[]>(queryKey, (old = []) =>
        old.map(group =>
          group.id === id ? { id, responses: updatedChoices } : group,
        ),
      )
    },
  })
}
